import React, { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
// mismas tarjetas, formulario y lista que el módulo de Catálogos
import style from "../../scss/Catalogos.module.scss";
import estiloUsuarios from "../../scss/Usuarios.module.scss";
import BotonIconoComponent, { AccionesTabla, BotonEditar, BotonEliminar } from "../../components/BotonIconoComponent";
import { crearUsuario, editarUsuario, eliminarUsuario, getUsuarios } from "../../Api/ApiUsuario";
import { etiquetaRol } from "../../constants/roles";
import { obtenerMensajeError } from "../../utils/mensajeError";
import { obtenerUsername, sincronizarDatosSesion } from "../../utils/sesion";

// el admin solo crea digitadores de su cuenta (el backend asigna el rol)
const FORMULARIO_VACIO = { username: "", password: "", correo: "", dobleFactor: true };

const UsuariosPages = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [formulario, setFormulario] = useState(FORMULARIO_VACIO);
  const [editando, setEditando] = useState(null); // usuario que se está editando
  const [guardando, setGuardando] = useState(false);
  // se guarda en estado para que la tabla se actualice si el admin cambia su propio nombre
  const [usuarioActual, setUsuarioActual] = useState(obtenerUsername);

  const cargarUsuarios = useCallback(async () => {
    try {
      setUsuarios(await getUsuarios());
    } catch (error) {
      Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
    }
  }, []);

  useEffect(() => {
    cargarUsuarios();
  }, [cargarUsuarios]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormulario({ ...formulario, [name]: type === "checkbox" ? checked : value });
  };

  const limpiarFormulario = () => {
    setFormulario(FORMULARIO_VACIO);
    setEditando(null);
  };

  const iniciarEdicion = (usuario) => {
    setEditando(usuario);
    setFormulario({
      username: usuario.username,
      password: "",
      correo: usuario.correo ?? "",
      dobleFactor: usuario.dobleFactor,
    });
  };

  const notificarExito = (mensaje) =>
    Swal.fire({ icon: "success", text: mensaje, timer: 1800, showConfirmButton: false });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      const respuesta = editando
        ? await editarUsuario(editando.id, {
            username: formulario.username.trim(),
            correo: formulario.correo,
            password: formulario.password,
            dobleFactor: formulario.dobleFactor,
          })
        : await crearUsuario(formulario);
      // si el admin se cambió su propio nombre, se actualiza la sesión del equipo
      if (editando?.username === usuarioActual) {
        const nuevoNombre = formulario.username.trim();
        sincronizarDatosSesion({ rol: editando.rol, username: nuevoNombre });
        setUsuarioActual(nuevoNombre);
      }
      notificarExito(respuesta.msg);
      limpiarFormulario();
      cargarUsuarios();
    } catch (error) {
      Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
    } finally {
      setGuardando(false);
    }
  };

  // activa/desactiva la verificación por correo (2FA) directamente desde la tabla
  const cambiarDobleFactor = (usuario) => {
    const activar = !usuario.dobleFactor;
    Swal.fire({
      title: `¿${activar ? "Activar" : "Desactivar"} la verificación por correo para ${usuario.username}?`,
      text: activar
        ? "Al iniciar sesión deberá ingresar el código que le llega al correo."
        : "Podrá iniciar sesión solo con usuario y contraseña.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sí, Confirmar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const respuesta = await editarUsuario(usuario.id, {
          correo: usuario.correo,
          dobleFactor: activar,
        });
        notificarExito(respuesta.msg);
        cargarUsuarios();
      } catch (error) {
        Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
      }
    });
  };

  const cambiarEstado = (usuario) => {
    const accion = usuario.estado ? "desactivar" : "activar";
    Swal.fire({
      title: `¿Seguro que deseas ${accion} a ${usuario.username}?`,
      text: usuario.estado ? "No podrá iniciar sesión hasta que lo vuelvas a activar." : "",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, Confirmar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const respuesta = await editarUsuario(usuario.id, {
          correo: usuario.correo,
          estado: !usuario.estado,
        });
        notificarExito(respuesta.msg);
        cargarUsuarios();
      } catch (error) {
        Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
      }
    });
  };

  const confirmarEliminar = (usuario) => {
    Swal.fire({
      title: `¿Eliminar a ${usuario.username}?`,
      text:
        "Esta acción no se puede deshacer. Los electores, votos y actas que registró se conservan, pero quedarán " +
        "sin registrador y solo los verá el administrador. Si solo quieres impedir que inicie sesión, desactívalo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const respuesta = await eliminarUsuario(usuario.id);
        notificarExito(respuesta.msg);
        if (editando?.id === usuario.id) limpiarFormulario();
        cargarUsuarios();
      } catch (error) {
        Swal.fire({ icon: "error", text: obtenerMensajeError(error) });
      }
    });
  };

  return (
    <div className={`container ${style.catalogos}`}>
      <h1 className={style.encabezado}>Usuarios</h1>
      <p className={style.descripcion}>
        Crea los digitadores de tu campaña. Todo lo que registren queda a tu nombre; el digitador solo puede registrar
        electores y ver los que él registró. Cada usuario necesita un correo para recibir el código de verificación al
        iniciar sesión.
      </p>

      <div className="row g-4">
        <div className="col-12 col-lg-4">
          <section className={style.tarjeta}>
            <h2 className={style.titulo}>{editando ? `Editar ${editando.username}` : "Nuevo digitador"}</h2>

            <form onSubmit={handleSubmit} className="row g-2">
              <div className="col-12">
                <label htmlFor="usuario-username" className={style.etiqueta}>
                  Usuario
                </label>
                <input
                  id="usuario-username"
                  name="username"
                  className="form-control"
                  value={formulario.username}
                  onChange={handleChange}
                  minLength={4}
                  maxLength={100}
                  autoComplete="off"
                  required
                />
              </div>
              <div className="col-12">
                <label htmlFor="usuario-correo" className={style.etiqueta}>
                  Correo {formulario.dobleFactor ? "(recibe el código de verificación)" : "(opcional)"}
                </label>
                <input
                  id="usuario-correo"
                  name="correo"
                  type="email"
                  className="form-control"
                  value={formulario.correo}
                  onChange={handleChange}
                  required={formulario.dobleFactor}
                />
              </div>
              <div className="col-12">
                <label htmlFor="usuario-password" className={style.etiqueta}>
                  {editando ? "Nueva contraseña (opcional)" : "Contraseña"}
                </label>
                <input
                  id="usuario-password"
                  name="password"
                  type="password"
                  className="form-control"
                  value={formulario.password}
                  onChange={handleChange}
                  minLength={8}
                  placeholder={editando ? "Déjala vacía para no cambiarla" : "Mínimo 8 caracteres"}
                  autoComplete="new-password"
                  required={!editando}
                />
              </div>
              <div className="col-12">
                <div className={`form-check form-switch ${estiloUsuarios.interruptor}`}>
                  <input
                    id="usuario-doble-factor"
                    name="dobleFactor"
                    type="checkbox"
                    role="switch"
                    className="form-check-input"
                    checked={formulario.dobleFactor}
                    onChange={handleChange}
                  />
                  <label htmlFor="usuario-doble-factor" className="form-check-label">
                    Verificación por correo (2FA) al iniciar sesión
                  </label>
                </div>
              </div>

              <div className={`col-12 ${style.botones}`}>
                <button type="submit" className={`btn btn-primary ${style.boton}`} disabled={guardando}>
                  {editando ? "Guardar cambios" : "Crear digitador"}
                </button>
                {editando && (
                  <button type="button" className={`btn btn-outline-secondary ${style.boton}`} onClick={limpiarFormulario}>
                    Cancelar
                  </button>
                )}
              </div>
            </form>
          </section>
        </div>

        <div className="col-12 col-lg-8">
          <section className={style.tarjeta}>
            <h2 className={style.titulo}>
              Usuarios registrados <span className={style.contador}>{usuarios.length}</span>
            </h2>

            <div className={`table-responsive ${style.lista} ${estiloUsuarios.listaUsuarios}`}>
              <table className="table table-hover table-striped align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Usuario</th>
                    <th scope="col" className="d-none d-xxl-table-cell">Correo</th>
                    <th scope="col">Rol</th>
                    <th scope="col">Estado</th>
                    <th scope="col">2FA</th>
                    <th scope="col" className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {usuarios.map((usuario) => (
                    <tr key={usuario.id} className={editando?.id === usuario.id ? "table-primary" : ""}>
                      <td>
                        {usuario.username}
                        {usuario.username === usuarioActual && <span className={estiloUsuarios.etiquetaTu}>(tú)</span>}
                        {/* salvo en pantallas muy anchas, el correo va debajo del usuario (la tarjeta es angosta) */}
                        <span className={`d-xxl-none ${estiloUsuarios.correoMovil}`}>{usuario.correo || "Sin correo"}</span>
                      </td>
                      <td className="d-none d-xxl-table-cell">{usuario.correo || <span className="text-muted">Sin correo</span>}</td>
                      <td>{etiquetaRol(usuario.rol)}</td>
                      <td>
                        <span className={`badge ${usuario.estado ? "bg-success" : "bg-secondary"}`}>
                          {usuario.estado ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td>
                        <div className="form-check form-switch mb-0">
                          <input
                            type="checkbox"
                            role="switch"
                            className="form-check-input"
                            checked={usuario.dobleFactor}
                            onChange={() => cambiarDobleFactor(usuario)}
                            aria-label={`Verificación por correo de ${usuario.username}`}
                            title={usuario.dobleFactor ? "2FA activo" : "2FA inactivo"}
                          />
                        </div>
                      </td>
                      <td>
                        {/* botones solo con ícono para que la tabla quepa en la tarjeta */}
                        <AccionesTabla>
                          <BotonEditar
                            etiqueta={`Editar a ${usuario.username}`}
                            onClick={() => iniciarEdicion(usuario)}
                          />
                          {usuario.username !== usuarioActual && (
                            <>
                              <BotonIconoComponent
                                icono={usuario.estado ? "mdi:account-off-outline" : "mdi:account-check-outline"}
                                etiqueta={`${usuario.estado ? "Desactivar" : "Activar"} a ${usuario.username}`}
                                variante={usuario.estado ? "neutro" : "exito"}
                                onClick={() => cambiarEstado(usuario)}
                              />
                              <BotonEliminar
                                etiqueta={`Eliminar a ${usuario.username}`}
                                onClick={() => confirmarEliminar(usuario)}
                              />
                            </>
                          )}
                        </AccionesTabla>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default UsuariosPages;
